import { useCallback, useEffect, useMemo, useState } from 'react';
import './sideoverlay.scss';

import { Box, Chip, Skeleton } from '@mui/material';
import agent from '@root/agent';
import MuitoolTip from '@root/components/Tooltip/materialUiTooltip';
import YesNo from '@root/components/YesNo';
import MuiTable, { DEFAULT_PAGE_SIZE } from '@root/components/table/table';
import DateFormatPipe from '@root/components/utilities/DateFormatPipe';
import ContentBox from '@root/components/v2/content/ContentBox';
import CircularCloseButton from '@root/components/v2/ui-elements/CircularCloseButton';
import Text from '@root/components/v2/ui-elements/Text';
import Tooltip from '@root/components/v2/ui-elements/Tooltip';
import HealthScoreBreakdown from '@root/pages/inventory1.3/Components/HealthScoreBreakdown';
import useEnhancedQuery from '@root/query/useEnhancedQuery';
import { layersColumnsImages, sbomColumnsImages, vulnerabilitiesImagesColumns } from './ImagesSideoverlayColumns';
import { imageColumns } from './imagesColumns';
import { columns } from './vulnerabilitiesColumns';
import { workloadColumns } from './workloadColumns';
import { componentsColumnsWorkload, vulnerabilitiesWorkloadColumns } from './workloadSideoverlayColumn';
// Remove MUI Drawer imports
// import Drawer from '@mui/material/Drawer';
import Loader from '@root/components/common/Loader';
import { fetchuserPolicyData, fetchuserPolicyTableData } from '@root/containers/dashboard/action';
import CveDetails from '@root/pages/workloload-scanning/CveDetails';
import JiraConfiguration from '@root/pages/workloload-scanning/Modal/JiraConfiguration';
import { capitalizeFirstLetter } from '@root/utilities/string';
import isEmpty from 'lodash/isEmpty';
import { Sparkles } from 'lucide-react';
import K8sOverlayHeaderIdentity from './K8sOverlayHeaderIdentity';
import isNumber from 'lodash/isNumber';
import isBoolean from 'lodash/isBoolean';
import isObject from 'lodash/isObject';
import StackedLayersSideoverlay from './StackedLayersSideoverlay';
import JIRAIcon from '@root/assets/svg/Kubernetes/JIRAIcon';
import PaginatedFilter from '@root/components/FilterNew/PaginatedFilter';
import HeaderBar from '@root/components/HeaderBarNew';
import { buildBreadcrumbUrl } from '@root/hooks/breadcrumbs/useBreadcrumbs';
import { useOCRPermissions } from '@root/hooks/permissions';
import useLatestScanTime from '@root/hooks/useLatestScanTime';
import useDebouncedValue from '@root/hooks/useDebouncedValue';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { cveSideoverlayWorkload } from '../SBOM/sbomColumns';
import { exportPipeline } from '../exportPipeline';
import DependencyTree from './DependencyTree';
// Add Sheet imports
import { Sheet, SheetContent } from '@root/components/v3/sheet';
import {
  COMPONENT_EXPORTED_COLS,
  COMPONENT_EXPORTED_HEADERNAME,
  CVE_IMAGE_EXPORTED_COLS,
  CVE_IMAGE_EXPORTED_HEADERNAME,
  CVE_WORKLOAD_EXPORTED_COLS,
  CVE_WORKLOAD_EXPORTED_HEADERNAME,
  IMAGES_VUL_EXPORTED_COLS,
  IMAGES_VUL_EXPORTED_HEADERNAME,
  IMAGES_WORKLOAD_EXPORTED_COLS,
  IMAGES_WORKLOAD_EXPORTED_HEADERNAME,
  SBOM_EXPORTED_COLS,
  SBOM_EXPORTED_HEADERNAME,
  SBOM_VUL_EXPORTED_COLS,
  SBOM_VUL_EXPORTED_HEADERNAME,
  SBOM_WORKLOAD_EXPORTED_COLS,
  SBOM_WORKLOAD_EXPORTED_HEADERNAME,
  VULNERABILITY_EXPORTED_COLS,
  VULNERABILITY_EXPORTED_HEADERNAME,
} from './ExportColumns';
import VulnerabilityRemediationAi from './VulnerabilityRemediationAi';
import StackedVulnerabilitiesSideOverlay from './workloadVulnerabilitySideoverlay';
import { Button } from '@root/components/v3/button';
import WorkloadCVETrend from './WorkloadCVETrend';
import SideOverlayTabMenu, { SideOverlayBody } from '@root/components/v2/SideOverlayTabMenu';
import { getVulnerabilityMenuCategories } from './tabMenu';

const runtimeColumn = [
  {
    field: 'image_tag',
    headerName: 'Image Tag',
    width: 250,
  },
  {
    field: 'path',
    headerName: 'Path',
    width: 250,
  },
  {
    field: 'in_use',
    headerName: 'In use',
    width: 250,
    renderCell: params => {
      return <Text font="medium">{params.row.in_use ? <YesNo yes /> : <YesNo no />}</Text>;
    },
  },
];

const images_key_list = ['Name', 'Related Workloads'];
const cve_workloads = ['Namespace', 'Workload Kind', 'Workload Name', 'Related Images', 'Pods'];

const vul_key_list = ['Vulnerability Id', 'Severity', 'Cvss Score', 'Exploitable', 'In Use', 'Fixable'];
const workloads_components = ['Name', 'Type', 'Related Images', 'Fix Version'];
const images_workloads = ['Namespace', 'Workload Kind', 'Workload Name'];
const images_sbom = ['Name', 'Type'];

// cves-details names a workload's images and pods differently from the per-workload API the columns and export were built on.
const toCveWorkloadRow = item => ({
  ...item,
  related_images: item.images,
  runtime_instances: item.pods?.map(pod_name => ({ pod_name })),
});

const formatSize = size => {
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(2)} KB`;
  if (size < 1024 * 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(2)} MB`;
  return `${(size / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

const SideOverlay = props => {
  const { selectedRow, onClose, type, isStacked = false, cveScope } = props;
  // The CVEs tab passes its environments and clusters and reads the CVE from cves-details; the inventory
  // overlays pass one workload's or node's CVE and keep the per-workload API.
  const isCveList = type === 'cves' && Boolean(cveScope);
  const [tab, setTab] = useState('details');
  const [isStackedLayerOpen, setIsStackedLayerOpen] = useState(false);
  const [isVulnerabilitySideOverlayOpen, setIsVulnerabilitySideOverlayOpen] = useState(false);
  const [selectedVulnerabilityRow, setSelectedVulnerabilityRow] = useState(props.selectedRow ?? {});
  const [openFilter, setOpenFilter] = useState('');
  const [searchString, setSearchString] = useState('');
  const debouncedSearchString = useDebouncedValue(searchString, 750);
  const [currentPage, setCurrentPage] = useState(1);
  const [WorkloadTimeRangeValue, setWorkloadTimeRangeValue] = useState(7);

  const { latestK8sScanTime } = useLatestScanTime();
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [showViperAi, setShowViperAi] = useState(false);
  const [open, setOpen] = useState(false);
  const history = useHistory();
  const [isJiraEnabled, setIsJiraEnabled] = useState(true);
  const [jiraErrorMsgs, setJiraErrorMsgs] = useState(null);
  const dispatch = useDispatch();
  const { isConfigureAllowed } = useOCRPermissions();
  const [isTicketIdPresent, setIsTicketIdPresent] = useState(false);
  const user_permission = JSON.parse(localStorage.getItem('User Policy') || '[]');
  const user = useSelector(state => state.user);
  const [selectedFilters, setSelectedFilters] = useState({
    evaluation: [],
    shield_severity: [],
    risk: [],
    cluster_name: [],
    pod: [],
    namespace: [],
    pods: [],
    timerange: [],
    related_images: [],
    container_name: [],
    name: [],
    related_workloads: [],
    cve_id: [],
    vulnerability_id: [],
    cvss_score: [],
    affected_workloads: [],
    affected_artifacts: [],
    exploitable: [],
    fixable: [],
    in_use: [],
    severity: [],
    type: [],
    fix_version: [],
  });

  let disableCreateJiraTicket = true;
  const jira_policy = useSelector(state => state.policyRes.data);
  useEffect(() => {
    if (!jira_policy || isEmpty(jira_policy)) {
      dispatch(fetchuserPolicyData);
      dispatch(fetchuserPolicyTableData);
    }
  }, [dispatch, jira_policy]);

  jira_policy.filter(itm => {
    if (user_permission?.includes('All Permissions')) {
      disableCreateJiraTicket = false;
    } else {
      if (itm.sub_menu_items === 'Create Jira Issues') {
        const temp = Object.keys(itm);
        user_permission.map(itm2 => {
          if (temp?.includes(itm2)) {
            disableCreateJiraTicket = itm[itm2] === 'False' ? false : false;
          }
        });
      }
    }
  });

  const assetId = useMemo(
    () =>
      `Kubernetes_CVES__${selectedRow.account_id}__${selectedRow.cve_id}____${selectedRow.cluster}__${selectedRow.component}__${selectedRow.workload_id}__${selectedRow.workload_name}__${selectedRow.workload_kind}`,
    [
      selectedRow.account_id,
      selectedRow.cluster,
      selectedRow.component,
      selectedRow.cve_id,
      selectedRow.workload_id,
      selectedRow.workload_kind,
      selectedRow.workload_name,
    ],
  );

  const { data: orgId, isFetching: isFetchingOrgId } = useEnhancedQuery(
    ['get_org_id', user.info.user.email],
    () =>
      agent.Jira_Notification.get_org_id({
        currentUserEmail: user.info.user.email,
      }),
    {
      enabled: true,
    },
  );

  const { data: projects, isFetching: isFetchingProjectDetails } = useEnhancedQuery(
    ['get_project_id', user.info.user.email],
    () =>
      agent.Jira_Notification.get_jira_projects({
        orgId: orgId?.data?.orgId,
      }),
    {
      enabled: Boolean(orgId),
    },
  );

  const { data: ticketDetails, isFetching: isFetchingTicketDetails } = useEnhancedQuery(
    ['get_ticket_details_cves', assetId, selectedRow],
    () =>
      agent.Jira_Notification.get_ticket_details({
        asset_id: assetId,
      }),
    {
      enabled: Boolean(!isFetchingOrgId) && Boolean(orgId),
    },
  );

  const { data: resourceTrend, isFetching: isFetchingResourceTrend } = useEnhancedQuery(
    ['get_workload_cve_trend', selectedRow, WorkloadTimeRangeValue],
    () =>
      agent.Kubernetes.get_workload_cve_trend({
        environment: selectedRow?.account_id,
        cluster: selectedRow?.cluster,
        // "workload_id": "external-dns/ReplicaSet/external-dns-7d899c69",
        workload_id: selectedRow?.workload_id,
        time_range: WorkloadTimeRangeValue,
        user_timezone: 'Asia/Kolkata',
        risk_spotlight: selectedRow?.risk_spotlight,
      }),
    {
      enabled: type === 'workloads',
    },
  );

  const { data: sbomDetails, isFetching: isFetchingSbomDetails } = useEnhancedQuery(
    ['get_sbom_table_details', selectedRow, tab, type, pageSize, currentPage, selectedFilters],
    () =>
      agent.Kubernetes.get_sbom_table_details({
        environment: selectedRow?.account_id,
        component_id: selectedRow?.name,
        cluster: selectedRow?.cluster,
        version: selectedRow.version,
        rows_per_page: pageSize,
        page_number: currentPage,
        export: false,
        risk_spotlight: selectedRow?.risk_spotlight,
        property_keys: {
          ...(tab === 'images' && {
            name: selectedFilters.name,
            related_workloads: selectedFilters.related_workloads,
          }),
          ...(tab === 'vulnerabilities' && {
            cve_id: selectedFilters.vulnerability_id,
            cvss_score: selectedFilters.cvss_score,
            exploitable: selectedFilters.exploitable,
            fixable: selectedFilters.fixable,
            in_use: selectedFilters.in_use,
            severity: selectedFilters.severity,
            affected_workloads: selectedFilters.affected_workloads,
            affected_artifacts: selectedFilters.affected_artifacts,
          }),
          ...(tab === 'workloads' && {
            namespace: selectedFilters.namespace,
            workload_kind: selectedFilters.workload_kind,
            workload_name: selectedFilters.workload_name,
          }),
        },
        tab: tab,
      }),
    {
      enabled: type === 'sbom',
    },
  );

  const { data: imageDetails, isFetching: isFetchingImageDetails } = useEnhancedQuery(
    ['get_images_table_details', selectedRow, type, tab, pageSize, currentPage, selectedFilters],
    () =>
      agent.Kubernetes.get_images_table_details({
        environment: selectedRow?.account_id,
        cluster: selectedRow?.cluster,
        image_id: selectedRow?.image_id ?? selectedRow?._id,
        image_name: selectedRow?.name,
        rows_per_page: pageSize,
        page_number: currentPage,
        export: false,
        risk_spotlight: selectedRow?.risk_spotlight,
        property_keys: {
          ...(tab === 'vulnerabilities' && {
            cve_id: selectedFilters.vulnerability_id,
            cvss_score: selectedFilters.cvss_score,
            exploitable: selectedFilters.exploitable,
            fixable: selectedFilters.fixable,
            in_use: selectedFilters.in_use,
            severity: selectedFilters.severity,
          }),
          ...(tab === 'workloads' && {
            namespace: selectedFilters.namespace,
            workload_kind: selectedFilters.workload_kind,
            workload_name: selectedFilters.workload_name,
          }),
          ...(tab === 'sbom' && {
            type: selectedFilters.type,
            name: selectedFilters.name,
          }),
        },
        tab: tab === 'sbom' ? 'sboms' : tab,
      }),
    {
      enabled: type === 'images' && tab !== 'layers',
    },
  );

  const { data: workloadsDetails, isFetching: isFetchingWorkloadsDetails } = useEnhancedQuery(
    ['get_workloads_table_details', selectedRow, type, tab, pageSize, currentPage, selectedFilters],
    () =>
      agent.Kubernetes.get_workloads_table_details({
        environment: selectedRow?.account_id,
        cluster: selectedRow?.cluster,
        workload_id: selectedRow?.workload_id,
        tab: tab,
        rows_per_page: pageSize,
        export: false,
        page_number: currentPage,
        risk_spotlight: selectedRow?.risk_spotlight,
        property_keys: {
          ...(tab === 'vulnerabilities' && {
            cve_id: selectedFilters.vulnerability_id,
            cvss_score: selectedFilters.cvss_score,
            exploitable: selectedFilters.exploitable,
            fixable: selectedFilters.fixable,
            in_use: selectedFilters.in_use,
            severity: selectedFilters.severity,
            affected_workloads: selectedFilters.affected_workloads,
            affected_artifacts: selectedFilters.affected_artifacts,
          }),
          ...(tab === 'images' && {
            name: selectedFilters.name,
            related_workloads: selectedFilters.related_workloads,
          }),
          ...(tab === 'components' && {
            name: selectedFilters.name,
            related_images: selectedFilters.related_images,
            type: selectedFilters.type,
            version: selectedFilters.fix_version,
          }),
        },
      }),
    {
      enabled: type === 'workloads',
    },
  );

  // The CVE sheet is opened from the Vulnerabilities tab, so deriving this instead
  // of setting it in an effect that skipped that very tab: previously `workLoadId`
  // was only filled while another tab was open, and stayed null for anyone who went
  // straight to Vulnerabilities — leaving Runtime Analysis to query a null workload.
  const workLoadId = useMemo(
    () => workloadsDetails?.data?.workload_id ?? selectedRow?.workload_id ?? null,
    [workloadsDetails?.data?.workload_id, selectedRow?.workload_id],
  );

  const dateFormat = useCallback(value => {
    const date = new Date(value);
    const formattedDate = date.toLocaleDateString('en-GB', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
      timeZone: 'UTC',
    });
    const parts = formattedDate.split(' ');
    const finalFormattedDate = `${parts[1]} ${parts[0]}, ${parts[2]}`;
    return finalFormattedDate;
  }, []);

  const jiraTicketDetails = useMemo(() => {
    if (isFetchingTicketDetails) return {};

    return {
      created_at: ticketDetails?.data?.ticketInfo?.createdDate
        ? dateFormat(ticketDetails?.data?.ticketInfo?.createdDate)
        : '-',
      last_updated: ticketDetails?.data?.ticketInfo?.lastUpdatedDate
        ? dateFormat(ticketDetails?.data?.ticketInfo?.lastUpdatedDate)
        : '-',
      status: ticketDetails?.data?.ticketInfo?.status,
      relatedTicket: ticketDetails?.data?.relatedTickets?.length,
      issueKey: ticketDetails?.data?.ticketInfo?.issueKey,
      due_date: ticketDetails?.data?.ticketInfo?.dueDate ? dateFormat(ticketDetails?.data?.ticketInfo?.dueDate) : '-',
    };
  }, [dateFormat, isFetchingTicketDetails, ticketDetails?.data]);

  useEffect(() => {
    if (isFetchingTicketDetails) {
      return;
    }
    setIsTicketIdPresent(ticketDetails?.data?.ticketInfo?.issueKey ? true : false);
  }, [isFetchingTicketDetails, setIsTicketIdPresent, ticketDetails?.data?.ticketInfo?.issueKey]);

  useEffect(() => {
    if (isFetchingOrgId || isFetchingProjectDetails) {
      return;
    }

    if (projects?.error) {
      setIsJiraEnabled(projects?.msg);
    }

    if (!orgId?.data?.orgId) {
      setIsJiraEnabled(false);
    } else {
      if (projects?.msg?.includes('JIRA is not enabled for this tenant, please enable from integrations')) {
        setIsJiraEnabled(false);
      } else if (projects?.msg?.includes('Jira token invalid or expired')) {
        setIsJiraEnabled(false);
        setJiraErrorMsgs(projects?.msg);
      } else if (!projects?.msg) {
        setIsJiraEnabled(true);
        setJiraErrorMsgs(null);
      } else {
        setIsJiraEnabled(false);
        setJiraErrorMsgs(null);
      }
    }
  }, [isFetchingOrgId, isFetchingProjectDetails, orgId, projects]);

  const { data: cvesDetails, isFetching: isFetchingCvesDetails } = useEnhancedQuery(
    [
      'get_cves_table_details',
      selectedRow,
      tab,
      type,
      pageSize,
      currentPage,
      selectedFilters,
      cveScope,
      isCveList && debouncedSearchString,
    ],
    () =>
      isCveList
        ? agent.Vulnerabilites.get_k8_cves_details({
            environments: cveScope.environments,
            clusters: cveScope.clusters,
            cve_id: selectedRow?.cve_id,
            tab: tab,
            ...(tab === 'details'
              ? { risk_spotlight: selectedRow?.risk_spotlight || false }
              : { search_val: debouncedSearchString, page_number: currentPage, rows_per_page: pageSize }),
          })
        : agent.Kubernetes.get_cves_table_details({
            environment: selectedRow?.account_id,
            cluster: selectedRow?.cluster,
            cve_id: selectedRow?.cve_id,
            workload_id: selectedRow?.workload_id || '',
            image_id: selectedRow?.image || '',
            tab: tab,
            rows_per_page: pageSize,
            page_number: currentPage,
            risk_spotlight: selectedRow?.risk_spotlight || false,
            export: false,
            property_keys: {
              ...(tab === 'images' && {
                name: selectedFilters.name,
                related_workloads: selectedFilters.related_workloads,
              }),
              ...(tab === 'workloads' && {
                related_images: selectedFilters.related_images,
                pod: selectedFilters.pods,
                namespace: selectedFilters.namespace,
                container_name: selectedFilters.container_name,
                workload_name: selectedFilters.workload_name,
                workload_kind: selectedFilters.workload_kind,
              }),
            },
          }),
    {
      enabled: type === 'cves',
    },
  );

  // A new search starts from the first page.
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchString]);

  const handlePageSizeChange = useCallback(_pageSize => {
    setCurrentPage(1);
    setPageSize(_pageSize);
  }, []);

  const handleOpenJiraModel = useCallback(() => {
    setOpen(true);
  }, []);

  const handleJiraRedirection = useCallback(
    ticketId => {
      const url = buildBreadcrumbUrl({
        path: `/notifications/jira?ticket_id=${ticketId}`,
        breadcrumbs: [
          { title: 'Kubernetes' },
          { title: 'Vulnerabilities', path: '/kubernetes-live/vulnerabilities' },
          { title: 'Jira' },
        ],
      });
      history.push(url);
    },
    [history],
  );

  const handleTabChangeReset = useCallback(() => {
    setPageSize(DEFAULT_PAGE_SIZE);
    setCurrentPage(1);
    setSearchString('');
    setOpenFilter(false);
    setSelectedFilters(prev => {
      const updatedFilters = Object.keys(prev).reduce((acc, key) => {
        acc[key] = [];
        return acc;
      }, {});

      return updatedFilters;
    });
  }, []);

  const handleMenuChange = useCallback(
    key => {
      setTab(key);
      handleTabChangeReset();
    },
    [handleTabChangeReset],
  );

  const handlePageChange = useCallback(
    page => {
      if (isFetchingCvesDetails) return;
      setCurrentPage(page);
    },
    [isFetchingCvesDetails],
  );

  const fetchCVEVulnerabilites = useCallback(
    async paginatedFilter => {
      const response = await agent.Kubernetes.get_cve_sideoverlay_keys({
        environment: selectedRow?.account_id,
        cluster: selectedRow?.cluster,
        tab: tab,
        risk_spotlight: selectedRow?.risk_spotlight,
        pod_name: selectedRow?.pod,
        cve_id: selectedRow?.cve_id ?? '',
        rows_per_page: paginatedFilter ? paginatedFilter?.rows_per_page : 20,
        page_number: paginatedFilter ? paginatedFilter?.page_number : 1,
        filter_particular_key: paginatedFilter
          ? paginatedFilter?.filter_particular_key?.toLowerCase().replace(/\s+/g, '_')
          : '',
        search_val: paginatedFilter ? paginatedFilter?.search_key : '',
        previous_filter: [],
      });
      return response;
    },
    [
      selectedRow?.account_id,
      selectedRow?.cluster,
      selectedRow?.cve_id,
      selectedRow?.pod,
      selectedRow?.risk_spotlight,
      tab,
    ],
  );

  const fetchWorkloadVulnerabilites = useCallback(
    async paginatedFilter => {
      const uiKey = paginatedFilter ? paginatedFilter?.filter_particular_key?.toLowerCase().replace(/\s+/g, '_') : '';
      const apiKey = uiKey === 'fix_version' ? 'version' : uiKey;
      const response = await agent.Kubernetes.get_workload_sideoverlay_keys({
        environment: selectedRow?.account_id,
        cluster: selectedRow?.cluster,
        tab: tab,
        risk_spotlight: selectedRow?.risk_spotlight,
        namespace: selectedRow?.namespace,
        workload_name: selectedRow?.workload_name ?? '',
        workload_kind: selectedRow.workload_kind ?? '',
        rows_per_page: paginatedFilter ? paginatedFilter?.rows_per_page : 20,
        page_number: paginatedFilter ? paginatedFilter?.page_number : 1,
        filter_particular_key: apiKey,
        search_val: paginatedFilter ? paginatedFilter?.search_key : '',
        previous_filter: [],
      });
      if (uiKey === 'fix_version' && response?.data && response.data.version && !response.data.fix_version) {
        response.data.fix_version = response.data.version;
      }
      return response;
    },
    [
      selectedRow?.account_id,
      selectedRow?.cluster,
      selectedRow?.namespace,
      selectedRow?.risk_spotlight,
      selectedRow.workload_kind,
      selectedRow?.workload_name,
      tab,
    ],
  );

  const fetchSbomVulnerabilities = useCallback(
    async paginatedFilter => {
      const response = await agent.Kubernetes.get_sbom_sideoverlay_keys({
        environment: selectedRow?.account_id,
        cluster: selectedRow?.cluster,
        tab: tab,
        risk_spotlight: selectedRow?.risk_spotlight,
        component_id: selectedRow.name,
        version: selectedRow.version,
        rows_per_page: paginatedFilter ? paginatedFilter?.rows_per_page : 20,
        page_number: paginatedFilter ? paginatedFilter?.page_number : 1,
        filter_particular_key: paginatedFilter
          ? paginatedFilter?.filter_particular_key?.toLowerCase().replace(/\s+/g, '_')
          : '',
        search_val: paginatedFilter ? paginatedFilter?.search_key : '',
        previous_filter: [],
      });
      return response;
    },
    [
      selectedRow?.account_id,
      selectedRow?.cluster,
      selectedRow.name,
      selectedRow?.risk_spotlight,
      selectedRow.version,
      tab,
    ],
  );

  const fetchImagesVulnerabilities = useCallback(
    async paginatedFilter => {
      const response = await agent.Kubernetes.get_images_sideoverlay_keys({
        environment: selectedRow?.account_id,
        cluster: selectedRow?.cluster,
        risk_spotlight: selectedRow?.risk_spotlight,
        tab: tab === 'sbom' ? 'sboms' : tab,
        image_id: selectedRow?.image_id ?? selectedRow?._id ?? '',
        image_name: selectedRow?.name,
        rows_per_page: paginatedFilter ? paginatedFilter?.rows_per_page : 20,
        page_number: paginatedFilter ? paginatedFilter?.page_number : 1,
        filter_particular_key: paginatedFilter
          ? paginatedFilter?.filter_particular_key?.toLowerCase().replace(/\s+/g, '_')
          : '',
        search_val: paginatedFilter ? paginatedFilter?.search_key : '',
        previous_filter: [],
      });
      return response;
    },
    [
      selectedRow?._id,
      selectedRow?.account_id,
      selectedRow?.cluster,
      selectedRow?.image_id,
      selectedRow?.name,
      selectedRow?.risk_spotlight,
      tab,
    ],
  );

  const _propertyValues = useMemo(() => {
    const cve_images_initial = {};
    const cve_workloads_initial = {};
    const workload_vul_initial = {};
    const workload_comp_initial = {};
    const image_workload_initial = {};
    const images_sbom_initial = {};

    images_key_list.forEach(key => {
      cve_images_initial[key] = {
        data: [],
        num_records: 0,
        has_more: false,
      };
    });
    cve_workloads.forEach(key => {
      cve_workloads_initial[key] = {
        data: [],
        num_records: 0,
        has_more: false,
      };
    });
    vul_key_list.forEach(key => {
      workload_vul_initial[key] = {
        data: [],
        num_records: 0,
        has_more: false,
      };
    });
    workloads_components.forEach(key => {
      workload_comp_initial[key] = {
        data: [],
        num_records: 0,
        has_more: false,
      };
    });
    images_workloads.forEach(key => {
      image_workload_initial[key] = {
        data: [],
        num_records: 0,
        has_more: false,
      };
    });
    images_sbom.forEach(key => {
      images_sbom_initial[key] = {
        data: [],
        num_records: 0,
        has_more: false,
      };
    });

    return tab === 'images'
      ? cve_images_initial
      : tab === 'vulnerabilities'
        ? workload_vul_initial
        : tab === 'components'
          ? workload_comp_initial
          : (type === 'images' || type === 'sbom') && tab === 'workloads'
            ? image_workload_initial
            : type !== 'images' && tab === 'workloads'
              ? cve_workloads_initial
              : images_sbom_initial;
  }, [tab, type]);

  const tableCount = useMemo(() => {
    if (isFetchingSbomDetails || isFetchingImageDetails || isFetchingWorkloadsDetails || isFetchingCvesDetails)
      return 0;
    switch (type) {
      case 'cves':
        return cvesDetails?.total_count;
      case 'workloads':
        return workloadsDetails?.total_count;
      case 'images':
        return imageDetails?.total_count;
      case 'sbom':
        return sbomDetails?.total_count;
      default:
        return 0;
    }
  }, [
    cvesDetails?.total_count,
    imageDetails?.total_count,
    isFetchingCvesDetails,
    isFetchingImageDetails,
    isFetchingSbomDetails,
    isFetchingWorkloadsDetails,
    sbomDetails?.total_count,
    type,
    workloadsDetails?.total_count,
  ]);

  const detailValue = useMemo(() => {
    if (isFetchingCvesDetails || isFetchingImageDetails || isFetchingSbomDetails || isFetchingWorkloadsDetails)
      return {};
    if (type === 'cves') {
      return cvesDetails?.data;
    } else if (type === 'sbom') {
      return sbomDetails?.data;
    } else if (type === 'images') {
      return imageDetails?.data;
    } else if (type === 'workloads') {
      return workloadsDetails?.data;
    }
  }, [
    cvesDetails?.data,
    imageDetails?.data,
    isFetchingCvesDetails,
    isFetchingImageDetails,
    isFetchingSbomDetails,
    isFetchingWorkloadsDetails,
    sbomDetails?.data,
    type,
    workloadsDetails?.data,
  ]);

  // The CVE row in the field names the shared Details view reads (workload rows call them differently).
  const cveDetailsRow = useMemo(
    () => ({
      ...selectedRow,
      fixed_in_version: selectedRow?.fixed_in_version ?? selectedRow?.fix_version,
      first_seen: selectedRow?.first_seen ?? selectedRow?.discovered_at,
      vulnerability_details: {
        description: detailValue?.description ?? selectedRow?.description,
        ...selectedRow?.vulnerability_details,
      },
    }),
    [detailValue?.description, selectedRow],
  );

  const _tableData = useMemo(() => {
    if (isFetchingSbomDetails || isFetchingImageDetails || isFetchingWorkloadsDetails || isFetchingCvesDetails) {
      return [];
    }
    if (tab === 'layers' && type === 'images') {
      return [];
    }
    const _data = sbomDetails?.data || imageDetails?.data || workloadsDetails?.data || cvesDetails?.data || [];

    if (tab !== 'details') {
      return (
        _data?.map((item, idx) => {
          return {
            ...(isCveList ? toCveWorkloadRow(item) : item),
            id: idx + 1,
          };
        }) ?? []
      );
    }

    if (tab === 'vulnerabilities') return (_data || _data?.vulnerabilities)?.map((v, i) => ({ ...v, id: i })) ?? [];
    if (tab === 'images')
      return (_data || _data?.images || _data?.affected_images)?.map((v, i) => ({ ...v, id: i })) ?? [];
    if (tab === 'workloads') {
      if (type === 'cves') {
        let arr = [];
        selectedRow?.affected_workloads?.map((item, idx) => {
          const element = item.split('/');
          arr.push({
            namespace: element[0],
            pod: element[1],
            id: idx + 1,
            container: element[2],
          });
        }) ?? [];

        return arr;
      }
      if (type === 'images') {
        let arr = [];
        selectedRow?.related_workloads?.map((item, idx) => {
          arr.push({
            namespace: item.namespace,
            pod: item.pod,
            id: idx + 1,
            container: item.container_name,
            workload: item.workload_id,
          });
        }) ?? [];

        return arr;
      }
      return (_data?.workloads || _data?.affected_workloads || [])?.map((v, i) => ({ ...v, id: i }));
    }

    if (tab === 'sbom') return _data?.artifacts?.map((v, i) => ({ ...v, id: i }));
    if (tab === 'components') return _data?.components?.map((v, i) => ({ ...v, id: i }));
    if (tab === 'layers') return _data?.layers?.map((v, i) => ({ ...v, id: i }));
    if (tab === 'runtime')
      return [
        { id: 1, image_tag: selectedVulnerabilityRow?.images?.[0], path: '/usr/local/bin/mode', in_use: true },
        { id: 2, image_tag: selectedVulnerabilityRow?.images?.[0], path: '/usr/local/bin/mode', in_use: true },
      ];
  }, [
    cvesDetails?.data,
    imageDetails?.data,
    isFetchingCvesDetails,
    isFetchingImageDetails,
    isCveList,
    isFetchingSbomDetails,
    isFetchingWorkloadsDetails,
    sbomDetails?.data,
    selectedRow?.affected_workloads,
    selectedRow?.related_workloads,
    selectedVulnerabilityRow?.images,
    tab,
    type,
    workloadsDetails?.data,
  ]);

  const handleSearchChange = useCallback(e => {
    setSearchString(e.target.value);
  }, []);

  // One case per sheet, each guarded on its own (type, tab) pair. The previous
  // version ended in `type !== 'workloads' || ... || type !== 'layers'` — true for
  // every value of `type` — so the details panel it guarded could never open.
  const handleRowClick = useCallback(
    r => {
      // MuiTable hands over the grid params; ignore a click that carries no row
      // rather than opening a sheet on an empty selection.
      const row = r?.row;
      if (!row) return;

      // Images > Layers opens the stacked layers sheet.
      if (type === 'images' && tab === 'layers') {
        setSelectedVulnerabilityRow(row);
        setIsStackedLayerOpen(true);
        return;
      }

      // Every Vulnerabilities tab (workloads / images / sbom) opens the CVE sheet.
      if (tab === 'vulnerabilities') {
        setSelectedVulnerabilityRow(row);
        setIsVulnerabilitySideOverlayOpen(true);
      }
    },
    [tab, type],
  );

  const handleCloseStackedLayers = useCallback(() => {
    setIsStackedLayerOpen(false);
    setSelectedVulnerabilityRow(null);
  }, []);

  const handleCloseStackedVulnerabilities = useCallback(() => {
    setIsVulnerabilitySideOverlayOpen(false);
    setSelectedVulnerabilityRow(null);
  }, []);

  const _columns = useMemo(() => {
    if (type === 'cves') {
      if (tab === 'workloads') return cveSideoverlayWorkload;
    }
    if (type === 'workloads') {
      if (tab === 'vulnerabilities') return vulnerabilitiesWorkloadColumns();
    }
    if (type === 'images') {
      if (tab === 'workloads') return cveSideoverlayWorkload?.filter(item => item.field !== 'pod');
      if (tab === 'vulnerabilities') return vulnerabilitiesImagesColumns();
    }
    if (tab === 'vulnerabilities') return columns(type === 'sbom');
    else if (tab === 'images') return imageColumns;
    else if (tab === 'workloads') return workloadColumns;
    else if (tab === 'sbom') return sbomColumnsImages;
    else if (tab === 'components') return componentsColumnsWorkload;
    else if (tab === 'layers') return layersColumnsImages;
    else if (tab === 'runtime') return runtimeColumn;
  }, [tab, type]);

  const _severityData = useMemo(() => {
    const _data = selectedRow;
    const breakupData = _data?.vulnerability_counts;
    const total_count =
      breakupData?.total ??
      breakupData?.critical + breakupData?.high + breakupData?.medium + breakupData?.low + (breakupData?.unknown ?? 0);

    return {
      data: {
        total_risk: total_count,
        risk_breakup: [
          {
            name: 'Critical',
            value: breakupData?.critical,
          },
          {
            name: 'High',
            value: breakupData?.high,
          },
          {
            name: 'Medium',
            value: breakupData?.medium,
          },
          {
            name: 'Low',
            value: breakupData?.low,
          },
        ],
      },
    };
  }, [selectedRow]);

  const detailsKey = useMemo(() => {
    if (isFetchingSbomDetails && isFetchingImageDetails && isFetchingWorkloadsDetails) return [];
    if (type === 'sbom' || type === 'images' || type === 'workloads' || type === 'cves') {
      const _data = sbomDetails?.data || imageDetails?.data || workloadsDetails?.data || cvesDetails?.data || {};

      return Object.keys(_data)?.filter(
        k =>
          k !== 'account_id' &&
          k !== 'vulnerability_counts' &&
          k !== 'workloads' &&
          k !== 'vulnerabilities' &&
          k !== 'layers' &&
          k !== 'components' &&
          k !== 'images' &&
          k !== 'affected_workloads' &&
          k !== 'affected_artifacts' &&
          k !== 'affected_images' &&
          k !== 'related_artifacts' &&
          k !== 'related_images' &&
          k !== 'artifacts' &&
          k !== 'related_workloads' &&
          k !== 'urls' &&
          k !== 'dependency_tree' &&
          k !== 'dependency_trees',
      );
    }
  }, [
    cvesDetails?.data,
    imageDetails?.data,
    isFetchingImageDetails,
    isFetchingSbomDetails,
    isFetchingWorkloadsDetails,
    sbomDetails?.data,
    type,
    workloadsDetails?.data,
  ]);

  const handleExportData = useCallback(
    async (type, tab) => {
      toast.info('Exporting data...');

      try {
        if (type === 'cves') {
          // cves-details has no export flag, so the export asks for every row on one page.
          const response = isCveList
            ? await agent.Vulnerabilites.get_k8_cves_details({
                environments: cveScope.environments,
                clusters: cveScope.clusters,
                cve_id: selectedRow?.cve_id,
                tab: tab,
                search_val: debouncedSearchString,
                page_number: 1,
                rows_per_page: tableCount || pageSize,
              })
            : await agent.Kubernetes.get_cves_table_details({
                environment: selectedRow?.account_id,
                cluster: selectedRow?.cluster,
                cve_id: selectedRow?.cve_id,
                workload_id: selectedRow?.workload_id,
                image_id: selectedRow?.image,
                tab: tab,
                rows_per_page: pageSize,
                page_number: currentPage,
                risk_spotlight: selectedRow?.risk_spotlight,
                export: true,
                property_keys: {
                  ...(tab === 'images' && {
                    name: selectedFilters.name,
                    related_workloads: selectedFilters.related_workloads,
                  }),
                  ...(tab === 'workloads' && {
                    related_images: selectedFilters.related_images,
                    pod: selectedFilters.pods,
                    namespace: selectedFilters.namespace,
                    container_name: selectedFilters.container_name,
                  }),
                },
              });

          if (response?.data) {
            const cols = tab === 'workloads' ? CVE_WORKLOAD_EXPORTED_COLS : CVE_IMAGE_EXPORTED_COLS;
            const headers = tab === 'workloads' ? CVE_WORKLOAD_EXPORTED_HEADERNAME : CVE_IMAGE_EXPORTED_HEADERNAME;

            const data = (isCveList ? response.data.map(toCveWorkloadRow) : response.data)?.map(item => {
              const _item = {};
              for (const key of cols) {
                if (item[key]) {
                  _item[key] = item[key];
                } else {
                  _item[key] = '-';
                }
              }

              return {
                ..._item,
                ...(tab === 'workloads' && {
                  related_images: JSON.stringify(_item['related_images']),
                  runtime_instances: JSON.stringify(_item['runtime_instances'].map(item => item.pod_name)),
                  vulnerability_counts: JSON.stringify(_item['vulnerability_counts']),
                }),
                ...(tab === 'images' && {
                  related_workloads: JSON.stringify(_item['related_workloads']?.map(item => item.workload_id)),
                  vulnerability_counts: JSON.stringify(_item['vulnerability_counts']),
                }),
              };
            });
            exportPipeline(data, cols, headers, `K8 CVE ${capitalizeFirstLetter(tab)}`);
          }

          toast.success('Data exported successfully.');
        }
        if (type === 'workloads') {
          const response = await agent.Kubernetes.get_workloads_table_details({
            environment: selectedRow?.account_id,
            cluster: selectedRow?.cluster,
            workload_id: selectedRow?.workload_id,
            tab: tab,
            rows_per_page: pageSize,
            export: false,
            page_number: currentPage,
            risk_spotlight: selectedRow?.risk_spotlight,
            property_keys: {
              ...(tab === 'vulnerabilities' && {
                cve_id: selectedFilters.vulnerability_id,
                cvss_score: selectedFilters.cvss_score,
                exploitable: selectedFilters.exploitable,
                fixable: selectedFilters.fixable,
                in_use: selectedFilters.in_use,
                severity: selectedFilters.severity,
                affected_workloads: selectedFilters.affected_workloads,
                affected_artifacts: selectedFilters.affected_artifacts,
              }),
              ...(tab === 'images' && {
                name: selectedFilters.name,
                related_workloads: selectedFilters.related_workloads,
              }),
              ...(tab === 'components' && {
                name: selectedFilters.name,
                related_images: selectedFilters.related_images,
                type: selectedFilters.type,
                version: selectedFilters.fix_version,
              }),
            },
          });

          if (response?.data) {
            const cols =
              tab === 'vulnerabilities'
                ? VULNERABILITY_EXPORTED_COLS
                : tab === 'images'
                  ? CVE_IMAGE_EXPORTED_COLS
                  : COMPONENT_EXPORTED_COLS;
            const headers =
              tab === 'vulnerabilities'
                ? VULNERABILITY_EXPORTED_HEADERNAME
                : tab === 'images'
                  ? CVE_IMAGE_EXPORTED_HEADERNAME
                  : COMPONENT_EXPORTED_HEADERNAME;

            const data = response.data?.map(item => {
              const _item = {};
              for (const key of cols) {
                if (_item[key] !== undefined || _item[key] !== null) {
                  _item[key] = item[key];
                } else {
                  _item[key] = '-';
                }
              }

              return {
                ..._item,
                ...(tab === 'vulnerabilities' && {
                  in_use: JSON.stringify(_item['in_use']) || '-',
                  exploitable: JSON.stringify(_item['exploitable']) || '-',
                  workload_kind: JSON.stringify(_item['workload_kind']) || '-',
                  workload_name: JSON.stringify(_item['workload_name']) || '-',
                  recommended_action: JSON.stringify(_item['recommended_action']) || '-',
                }),
                ...(tab === 'images' && {
                  related_workloads: JSON.stringify(_item['related_workloads']?.map(item => item.workload_id)),
                  vulnerability_counts: JSON.stringify(_item['vulnerability_counts']),
                }),

                ...(tab === 'components' && {
                  version: JSON.stringify(_item['version']) || JSON.stringify(_item['fix_version']) || '-',
                  related_images: JSON.stringify(_item['images']) || JSON.stringify(_item['related_images']) || '-',
                  vulnerability_counts: JSON.stringify(_item['vulnerability_counts']),
                }),
              };
            });
            exportPipeline(data, cols, headers, `K8 Workload ${capitalizeFirstLetter(tab)}`);
          }

          toast.success('Data exported successfully.');
        }
        if (type === 'images') {
          const response = await agent.Kubernetes.get_images_table_details({
            environment: selectedRow?.account_id,
            cluster: selectedRow?.cluster,
            image_id: selectedRow?.image_id ?? selectedRow?._id,
            image_name: selectedRow?.name,
            rows_per_page: pageSize,
            page_number: currentPage,
            export: false,
            risk_spotlight: selectedRow?.risk_spotlight,
            property_keys: {
              ...(tab === 'vulnerabilities' && {
                cve_id: selectedFilters.vulnerability_id,
                cvss_score: selectedFilters.cvss_score,
                exploitable: selectedFilters.exploitable,
                fixable: selectedFilters.fixable,
                in_use: selectedFilters.in_use,
                severity: selectedFilters.severity,
              }),
              ...(tab === 'workloads' && {
                pod: selectedFilters.pod,
                namespace: selectedFilters.namespace,
                container_name: selectedFilters.container_name,
              }),
              ...(tab === 'sbom' && {
                type: selectedFilters.type,
                name: selectedFilters.name,
              }),
            },
            tab: tab === 'sbom' ? 'sboms' : tab,
          });

          if (response?.data) {
            const cols =
              tab === 'vulnerabilities'
                ? IMAGES_VUL_EXPORTED_COLS
                : tab === 'workloads'
                  ? IMAGES_WORKLOAD_EXPORTED_COLS
                  : tab === 'sbom'
                    ? SBOM_EXPORTED_COLS
                    : COMPONENT_EXPORTED_COLS;
            const headers =
              tab === 'vulnerabilities'
                ? IMAGES_VUL_EXPORTED_HEADERNAME
                : tab === 'workloads'
                  ? IMAGES_WORKLOAD_EXPORTED_HEADERNAME
                  : tab === 'sbom'
                    ? SBOM_EXPORTED_HEADERNAME
                    : COMPONENT_EXPORTED_HEADERNAME;

            const data = response.data?.map(item => {
              const _item = {};
              for (const key of cols) {
                if (_item[key] !== undefined || _item[key] !== null) {
                  _item[key] = item[key];
                } else {
                  _item[key] = '-';
                }
              }

              return {
                ..._item,
                ...(tab === 'vulnerabilities' && {
                  affected_workloads: JSON.stringify(_item['affected_workloads']),
                  affected_artifacts: JSON.stringify(_item['affected_artifacts']),
                }),
                ...(tab === 'workloads' && {
                  related_images: JSON.stringify(_item['related_images']),
                  vulnerability_counts: JSON.stringify(_item['vulnerability_counts']),
                }),
                ...(tab === 'sbom' && {
                  locations: JSON.stringify(_item['locations'].map?.(v => v.path)),
                  vulnerability_counts: JSON.stringify(_item['vulnerability_counts']),
                }),
              };
            });
            exportPipeline(data, cols, headers, `K8 Images ${capitalizeFirstLetter(tab)}`);
          }

          toast.success('Data exported successfully.');
        }
        if (type === 'sbom') {
          const response = await agent.Kubernetes.get_sbom_table_details({
            environment: selectedRow?.account_id,
            component_id: selectedRow?.name,
            cluster: selectedRow?.cluster,
            version: selectedRow.version,
            rows_per_page: pageSize,
            page_number: currentPage,
            export: true,
            risk_spotlight: selectedRow?.risk_spotlight,
            property_keys: {
              ...(tab === 'images' && {
                name: selectedFilters.name,
                related_workloads: selectedFilters.related_workloads,
              }),
              ...(tab === 'vulnerabilities' && {
                cve_id: selectedFilters.vulnerability_id,
                cvss_score: selectedFilters.cvss_score,
                exploitable: selectedFilters.exploitable,
                fixable: selectedFilters.fixable,
                in_use: selectedFilters.in_use,
                severity: selectedFilters.severity,
                affected_workloads: selectedFilters.affected_workloads,
                affected_artifacts: selectedFilters.affected_artifacts,
              }),
            },
            tab: tab,
          });

          if (response?.data) {
            const cols =
              tab === 'vulnerabilities'
                ? SBOM_VUL_EXPORTED_COLS
                : tab === 'images'
                  ? CVE_IMAGE_EXPORTED_COLS
                  : SBOM_WORKLOAD_EXPORTED_COLS;
            const headers =
              tab === 'vulnerabilities'
                ? SBOM_VUL_EXPORTED_HEADERNAME
                : tab === 'images'
                  ? CVE_IMAGE_EXPORTED_HEADERNAME
                  : SBOM_WORKLOAD_EXPORTED_HEADERNAME;

            const data = response.data?.map(item => {
              const _item = {};
              for (const key of cols) {
                if (_item[key] !== undefined || _item[key] !== null) {
                  _item[key] = item[key];
                } else {
                  _item[key] = '-';
                }
              }

              return {
                ..._item,

                ...(tab === 'vulnerabilities' && {
                  affected_workloads: JSON.stringify(_item['affected_workloads']?.map(item => item.workload_id)),
                }),

                ...(tab === 'workloads' && {
                  vulnerability_counts: JSON.stringify(_item['vulnerability_counts']),
                }),
                ...(tab === 'images' && {
                  vulnerability_counts: JSON.stringify(_item['vulnerability_counts']),
                  related_workloads: JSON.stringify(_item['related_workloads']?.map(item => item.workload_id)),
                }),
                ...(tab === 'sbom' && {
                  locations: JSON.stringify(_item['locations'].map?.(v => v.path)),
                  vulnerability_counts: JSON.stringify(_item['vulnerability_counts']),
                }),
              };
            });
            exportPipeline(data, cols, headers, `K8 SBOM ${capitalizeFirstLetter(tab)}`);
          }

          toast.success('Data exported successfully.');
        }
      } catch (err) {
        console.log(err);
        toast.error(`Failed to export data. ${err.message}`);
      }
    },
    [
      isCveList,
      cveScope,
      debouncedSearchString,
      tableCount,
      selectedRow?.account_id,
      selectedRow?.cluster,
      selectedRow?.cve_id,
      selectedRow?.risk_spotlight,
      selectedRow?.workload_id,
      selectedRow?.image_id,
      selectedRow?.image,
      selectedRow?._id,
      selectedRow?.name,
      selectedRow.version,
      pageSize,
      currentPage,
      selectedFilters.name,
      selectedFilters.related_workloads,
      selectedFilters.related_images,
      selectedFilters.pods,
      selectedFilters.namespace,
      selectedFilters.container_name,
      selectedFilters.vulnerability_id,
      selectedFilters.cvss_score,
      selectedFilters.exploitable,
      selectedFilters.fixable,
      selectedFilters.in_use,
      selectedFilters.severity,
      selectedFilters.affected_workloads,
      selectedFilters.affected_artifacts,
      selectedFilters.type,
      selectedFilters.fix_version,
      selectedFilters.pod,
    ],
  );

  const headerTitle = (type === 'cves' && selectedRow?.cve_id) || selectedRow?.cluster;

  const jiraTicketButton = isFetchingTicketDetails ? (
    <Skeleton variant="rectangular" width={120} height={32} style={{ borderRadius: '8px' }} />
  ) : (
    <MuitoolTip
      label={
        !isJiraEnabled && jiraErrorMsgs === 'Jira token invalid or expired' ? (
          <div style={{ textAlign: 'center' }}>
            {jiraErrorMsgs}
            <br /> please re-configure from
            <Link style={{ marginLeft: '5px' }} to="/notifications/channels">
              Channels
            </Link>
          </div>
        ) : !isJiraEnabled && jiraErrorMsgs !== (null || '') ? (
          <div style={{ textAlign: 'center' }}>
            JIRA is not enabled for this tenant,
            <br /> please enable from
            <Link style={{ marginLeft: '5px' }} to="/notifications/channels">
              Channels
            </Link>
          </div>
        ) : (
          !isConfigureAllowed && `No permission available. Please contact Organization Admin`
        )
      }>
      <Button
        variant={'outline'}
        className="h-8 text-[#1a202c]"
        disabled={(!isJiraEnabled && jiraErrorMsgs !== '') || !isJiraEnabled || !isConfigureAllowed}
        data-testid="click-k8s-pages-sideoverlay-1471"
        data-clickable="true"
        onClick={
          disableCreateJiraTicket ||
          (!isJiraEnabled && jiraErrorMsgs === 'Jira token invalid or expired') ||
          (!isJiraEnabled && jiraErrorMsgs !== (null || ''))
            ? () => {}
            : () => {
                if (isTicketIdPresent) {
                  handleJiraRedirection(jiraTicketDetails?.issueKey);
                } else {
                  handleOpenJiraModel();
                }
              }
        }>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}>
          <JIRAIcon />
          <p className="m-0 p-0 text-[12px] font-manrope  text-[var(--Secondary-400,#484d56)] text-center  font-medium leading-normal ">
            {isTicketIdPresent ? jiraTicketDetails?.issueKey : 'Create a Ticket'}
          </p>
        </div>
      </Button>
    </MuitoolTip>
  );

  return (
    <div className="k8Vulnebarlaity">
      <div className={`Resources__Backdrop2`} style={{ backgroundColor: 'transparent', top: '0px', height: '55px' }} />

      <div className={`Resources__Backdrop2`} />
      <div
        className={`Resources__DataSidebar2 Resources__DataSidebar2--menu ${isStacked ? 'Resources__DataStackedSidebar2' : ''}`}>
        {/* One row: what the panel is about on the left, every action flush right. */}
        <div className="Resources__SidebarHeader K8sOverlay__Header">
          <K8sOverlayHeaderIdentity type={type} selectedRow={selectedRow} title={headerTitle} />

          <div className="K8sOverlay__Actions">
            {type === 'cves' && jiraTicketButton}
            {type === 'cves' && (
              // Same icon button as Viper AI in the top nav bar; it toggles the remediation view in place of the tabs.
              <Tooltip title="Viper AI (Beta)" isIcon={true} placement="bottom">
                <button
                  type="button"
                  aria-label="Viper AI"
                  aria-pressed={showViperAi}
                  className={`K8sOverlay__IconButton${showViperAi ? ' K8sOverlay__IconButton--active' : ''}`}
                  onClick={() => setShowViperAi(prev => !prev)}>
                  <Sparkles size={16} />
                </button>
              </Tooltip>
            )}
            <CircularCloseButton onClick={() => onClose()} />
          </div>
        </div>

        <div className="Resources__SidebarContent CustomScrollbar">
          {type === 'cves' && showViperAi ? (
            <VulnerabilityRemediationAi
              selectedRow={selectedRow}
              detailValue={detailValue}
              onClose={() => setShowViperAi(false)}
            />
          ) : (
            <>
              <SideOverlayBody
                menu={
                  <SideOverlayTabMenu
                    categories={getVulnerabilityMenuCategories(type, isStacked, isCveList)}
                    activeTab={tab}
                    onChange={handleMenuChange}
                    ariaLabel="Vulnerability details sections"
                  />
                }>
                <div className="Resources__SidebarTabContent CustomScrollbar">
                  {!isStacked && (
                    <>
                      {tab === 'details' && type === 'cves' && (
                        // Same Details view as the workload Vulnerabilities CVE overlay.
                        <>
                          {/* cves-details has no dependency tree or component, so the CVEs tab skips the card. */}
                          {!isCveList && (
                            <Box padding={'15px 15px 0'}>
                              <ContentBox>
                                <DependencyTree
                                  dependencyData={selectedRow?.dependency_tree || selectedRow?.dependency_trees}
                                  component={selectedRow?.component}
                                />
                              </ContentBox>
                            </Box>
                          )}
                          <CveDetails selectedRow={cveDetailsRow} />
                        </>
                      )}
                      {tab === 'details' &&
                        type !== 'cves' &&
                        (isFetchingImageDetails || isFetchingSbomDetails || isFetchingWorkloadsDetails ? (
                          <div style={{ width: '100%', height: '100%', padding: '15px' }}>
                            <Loader />
                          </div>
                        ) : (
                          // No viewport-based heights here: Resources__SidebarTabContent is the one scroll area, so
                          // the details grow with their content instead of scrolling inside a second scrollbar.
                          <Box
                            display={'grid'}
                            padding={'15px'}
                            gap={'15px'}
                            alignItems={'start'}
                            gridTemplateColumns={'70% 29%'}>
                            <ContentBox>
                              {detailsKey?.map(k => {
                                const formattedKey = k
                                  .split('_')
                                  .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                                  .join(' ');

                                return (
                                  <div
                                    key={k}
                                    style={{
                                      display: 'flex',
                                      marginBottom: '8px',
                                      fontSize: '13px',
                                      // Top-aligned, so the label stays level with the first line of a wrapped value.
                                      alignItems: 'flex-start',
                                    }}>
                                    <span
                                      style={{
                                        fontWeight: '600',
                                        color: 'rgb(85, 85, 85)',
                                        marginRight: '8px',
                                        minWidth: '150px',
                                        fontSize: '14px',
                                        width: '12%',
                                        flexShrink: 0,
                                      }}>
                                      {formattedKey}:
                                    </span>
                                    {/* Values such as Runtime Instances are long JSON with no spaces: let them shrink
                                        below that width and break anywhere, so they wrap instead of scrolling sideways. */}
                                    <span style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere', color: '#484d56' }}>
                                      {k === 'last_scanned_at' ? (
                                        DateFormatPipe(latestK8sScanTime)
                                      ) : k === 'clusters' && Array.isArray(detailValue[k]) ? (
                                        detailValue[k].map(item => `${item.cluster} (${item.account_id})`).join(', ') ||
                                        '-'
                                      ) : k === 'size' ? (
                                        <Text font="medium">{formatSize(detailValue[k])}</Text>
                                      ) : k === 'status' ? (
                                        <Chip
                                          label={detailValue[k] === 'active' ? 'Active' : detailValue[k]}
                                          sx={{
                                            fontSize: '12px',
                                            fontWeight: '400',
                                            fontFamily: 'Manrope',
                                            height: '20px',
                                            color: detailValue[k] === 'active' ? '#0d5230' : '#FF6A7C',
                                            backgroundColor: detailValue[k] === 'active' ? '#acf3d9' : '#FF6A7C',
                                            borderRadius: '8px',
                                          }}
                                        />
                                      ) : k === 'last_scan' ||
                                        k === 'last_updated' ||
                                        k === 'last_modified' ||
                                        k === 'created_at' ||
                                        k === 'activated_at' ||
                                        k === 'discovered_at' ? (
                                        DateFormatPipe(detailValue[k])
                                      ) : isNumber(detailValue[k]) ||
                                        isBoolean(detailValue[k]) ||
                                        isObject(detailValue[k]) ? (
                                        capitalizeFirstLetter(JSON.stringify(detailValue[k]))
                                      ) : (
                                        (detailValue[k] ?? '-')
                                      )}
                                    </span>
                                  </div>
                                );
                              })}
                            </ContentBox>
                            <Box display={'flex'} flexDirection={'column'} gap={'15px'}>
                              <HealthScoreBreakdown
                                title={'Vulnerabilities By Severity'}
                                health_score_breakdown={_severityData}
                                isFetchingHealthScoreBreakdown={
                                  isFetchingSbomDetails || isFetchingImageDetails || isFetchingWorkloadsDetails
                                }
                                isKubernetes={true}
                                count_msg={'Vulnerabilities'}
                                infoMsg={''}
                                height={'398px'}
                              />
                              {type == 'workloads' && (
                                <WorkloadCVETrend
                                  resource_trend={resourceTrend}
                                  isFetchingResourceTrend={isFetchingResourceTrend}
                                  setWorkloadTimeRangeValue={setWorkloadTimeRangeValue}
                                  WorkloadTimeRangeValue={WorkloadTimeRangeValue}
                                />
                              )}
                            </Box>
                          </Box>
                        ))}
                      {tab !== 'details' && tab !== 'layers' && (
                        <>
                          <Box padding={'15px'} overflow={'auto'} height={'calc( 100vh - 150px)'}>
                            <HeaderBar
                              searchBar
                              searchBarValue={searchString}
                              setSearchTerm={setSearchString}
                              searchBarOnChange={handleSearchChange}
                              // cves-details takes no filters, so the CVEs tab overlay has no filter button.
                              setOpenFilter={isCveList ? undefined : setOpenFilter}
                              openFilter={openFilter}
                              exportButton={true}
                              disableExportMenu
                              onSearchBarSubmit={e => e.preventDefault()}
                              onExportButtonClick={() => handleExportData(type, tab)}
                              marginBottom={'15px'}
                            />

                            <PaginatedFilter
                              openFilter={openFilter}
                              option={_propertyValues}
                              setSelectedFilters={setSelectedFilters}
                              selectedFilters={selectedFilters}
                              fetchFilterPropertyValues={
                                type === 'cves'
                                  ? fetchCVEVulnerabilites
                                  : type === 'workloads'
                                    ? fetchWorkloadVulnerabilites
                                    : type === 'sbom'
                                      ? fetchSbomVulnerabilities
                                      : fetchImagesVulnerabilities
                              }
                              defaultValue={{}}
                              disablePortal={true}
                              isKubernetesInventory={true}
                            />

                            <div style={{ position: 'relative', display: 'flex', flexDirection: 'row' }}>
                              <Box display="flex" flexDirection="row" flex={1}>
                                <MuiTable
                                  columns={_columns}
                                  rows={_tableData}
                                  LoaderState={
                                    isFetchingSbomDetails ||
                                    isFetchingImageDetails ||
                                    isFetchingWorkloadsDetails ||
                                    isFetchingCvesDetails
                                  }
                                  paginationMode="server"
                                  sortingMode="server"
                                  total_size={tableCount}
                                  onRowClick={handleRowClick}
                                  page={currentPage}
                                  onPageChange={handlePageChange}
                                  noDataMessage={`No ${capitalizeFirstLetter(tab)} present!`}
                                  pageSize={pageSize}
                                  onPageSizeChange={handlePageSizeChange}
                                  rowHeight={49.9}
                                  isDynamicHeight={true}
                                  tableHeight={'637px'}
                                />
                              </Box>
                            </div>

                            {/* Both sheets open from inside this overlay's own panel
                                (`.Resources__DataSidebar2`, a fixed z-index 300 element
                                over a z-index 250 backdrop), so they have to be lifted
                                above it — at the sheet's default z-50 only the dark
                                overlay showed and the panel itself stayed hidden
                                behind this one.

                                The height override goes with it: the sheet is pinned
                                55px from the top yet sized `h-full`, so its last 55px —
                                the bottom of the card inside — fell below the viewport. */}

                            {/* Replace CommonDrawer with Sheet for layers */}
                            <Sheet open={isStackedLayerOpen} onOpenChange={setIsStackedLayerOpen}>
                              <SheetContent
                                side="right"
                                className="w-[70%] h-[calc(100dvh-55px)] p-0 overflow-hidden z-[400]"
                                overlayClassName="z-[400]"
                                onInteractOutside={handleCloseStackedLayers}>
                                <StackedLayersSideoverlay
                                  onClose={handleCloseStackedLayers}
                                  selectedRow={selectedVulnerabilityRow}
                                />
                              </SheetContent>
                            </Sheet>

                            {/* Replace StackedCommonDrawer with Sheet for vulnerabilities */}
                            <Sheet
                              open={isVulnerabilitySideOverlayOpen}
                              onOpenChange={setIsVulnerabilitySideOverlayOpen}>
                              <SheetContent
                                side="right"
                                className="w-[50%] h-[calc(100dvh-55px)] p-0 overflow-hidden z-[400]"
                                hideClose={true}
                                overlayClassName="z-[400]"
                                onInteractOutside={handleCloseStackedVulnerabilities}>
                                <StackedVulnerabilitiesSideOverlay
                                  onClose={handleCloseStackedVulnerabilities}
                                  selectedRow={selectedVulnerabilityRow}
                                  isFetchingWorkloadsDetails={isFetchingWorkloadsDetails}
                                  workLoadId={workLoadId}
                                />
                              </SheetContent>
                            </Sheet>
                          </Box>
                        </>
                      )}
                      {tab === 'layers' && (
                        <Box padding={'15px'}>
                          <StackedLayersSideoverlay
                            onClose={handleCloseStackedLayers}
                            selectedRow={selectedRow}
                            isEnabled={type === 'images' && tab === 'layers'}
                          />
                        </Box>
                      )}
                    </>
                  )}
                </div>
              </SideOverlayBody>
              <JiraConfiguration
                open={open}
                setOpen={setOpen}
                mode="WorkloadMain"
                rowData={selectedRow}
                assetId={assetId}
                orgId={orgId}
                projects={projects}
                isFetchingOrgId={isFetchingOrgId}
                isFetchingProjectDetails={isFetchingProjectDetails}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SideOverlay;
